import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Plus } from "lucide-react";
import { useState } from "react";
import type { Project, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { Fab } from "@/components/shell/Fab";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { userLabel, useUserDirectory } from "@/modules/tasks/directories";
import { projectProject } from "@/modules/tasks/projections";
import { ProjectForm } from "@/modules/tasks/screens/ProjectForm";
import type { SectionScreenProps } from "@/modules/types";

/**
 * One project's "Archive" action, or its "Archived" badge once it already
 * is one — the `isArchived` column doing double duty the same way
 * `ProductList.tsx`'s thumbnail column carries the selection checkbox
 * (`SelectableThumbnail`'s own comment on why `renderOverride` is the seam
 * for this rather than a column `DataTable` has no spare slot for).
 *
 * `stopPropagation` for the same reason that column does: the row's own
 * `onClick` opens the edit sheet, and archiving should not also do that.
 */
function ArchiveCell({
  project,
  canManage,
}: {
  project: Project;
  canManage: boolean;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  if (project.isArchived) {
    return <Badge variant="secondary">Archived</Badge>;
  }
  if (!canManage) return <span>No</span>;

  return (
    <div
      className="flex items-center gap-1.5"
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          setRejection(null);
          void data.tasks.archiveProject(project.id).then((result) => {
            if (result.ok) {
              void queryClient.invalidateQueries({ queryKey: ["tasks"] });
            } else {
              setRejection(result.rejection);
            }
          });
        }}
      >
        <Archive />
        Archive
      </Button>
      {rejection ? (
        <span className="text-destructive text-xs">{rejection.message}</span>
      ) : null}
    </div>
  );
}

/**
 * M7-13: the projects list, plus create, edit and archive (#399).
 *
 * `TasksRepository.list` is filtered against `projectId`, an id — this
 * screen is where someone finds out which id is `"OPS"`. There is no
 * per-project detail screen; a project's own tasks are reached by filtering
 * `All Tasks` or `Board` to it, not by drilling in here — which is also why
 * "Edit" lives in the same sheet as "New project" rather than a detail
 * screen this module does not otherwise need.
 */
export function Projects({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const permissions = usePermissions();
  const canManage = can(permissions, "tasks.project.manage");

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  const usersQuery = useUserDirectory();
  const projectsQuery = useQuery({
    queryKey: ["tasks", "projects", filter, sort, limit, cursor],
    queryFn: () => data.tasks.listProjects({ filter, sort, limit, cursor }),
  });

  if (usersQuery.isPending || projectsQuery.isPending) {
    return <TableSkeleton />;
  }

  if (usersQuery.isError) {
    return (
      <LoadFailed
        message={usersQuery.error.message}
        onRetry={() => {
          void usersQuery.refetch();
        }}
      />
    );
  }

  if (projectsQuery.isError) {
    return (
      <LoadFailed
        message={projectsQuery.error.message}
        onRetry={() => {
          void projectsQuery.refetch();
        }}
      />
    );
  }

  const users = usersQuery.data.items;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        Hidden without `tasks.project.manage` — a courtesy, not the
        enforcement: `POST/PATCH /api/tasks/projects` calls
        `requirePermission()` regardless, and a rejection from it still
        renders through `ProjectForm`'s own rejection text.
      */}
      {canManage ? (
        <>
          <div className="max-md:hidden flex shrink-0 justify-end border-b px-4 py-2">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setCreating(true);
              }}
            >
              <Plus />
              New project
            </Button>
          </div>
          <Fab
            label="New project"
            icon={<Plus aria-hidden />}
            onClick={() => {
              setCreating(true);
            }}
          />
        </>
      ) : null}

      <ProjectForm mode="create" open={creating} onOpenChange={setCreating} />

      {editing ? (
        <ProjectForm
          key={editing.id}
          mode="edit"
          project={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}

      <DataTable
        fields={fields}
        rows={projectsQuery.data.items}
        getRowId={(project) => project.id}
        project={projectProject}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={
          canManage
            ? (project) => {
                setEditing(project);
              }
            : undefined
        }
        renderOverride={(field, project) => {
          if (field.name === "ownerUserId") {
            return <span>{userLabel(users, project.ownerUserId)}</span>;
          }
          if (field.name === "isArchived") {
            return <ArchiveCell project={project} canManage={canManage} />;
          }
          return undefined;
        }}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={projectsQuery.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
